import React from 'react';
import { toast } from 'sonner';
import { Ban, FileText, Mail, Share2 } from 'lucide-react';
import { API } from '../../../lib/api';
import { sharePdfToWhatsApp } from '../../../lib/waShare';
import { ghost, rs, safe, useApi } from '../../../pages/admin/purchasing/ui';

const label = (n) => (n.party === 'supplier' ? (n.kind === 'credit' ? 'Supplier credit note' : 'Purchase debit note') : (n.kind === 'credit' ? 'Credit note' : 'Debit note'));
export const noteLabel = label;

// PDF / WhatsApp / Email / Cancel for one note.
export const NoteActions = ({ n, canEdit, onChanged }) => {
  const { call } = useApi();
  const pdf = `${API}/notes/${n.id}/pdf`;
  const whatsapp = safe(async () => {
    const { pdf_url: u } = await call('post', `/notes/${n.id}/share-link`);
    const link = u.startsWith('http') ? u : `${window.location.origin}${u}`;
    const c = n.customer_snapshot || {};
    const msg = `Dear ${c.name || 'Customer'},\n\nWe have issued ${label(n)} ${n.number} for ${rs(n.totals.grand_total)} against invoice ${n.invoice_number} (${n.reason}).\n\nView it here: ${link}`;
    await sharePdfToWhatsApp({ getPdf: async () => call('get', `/notes/${n.id}/pdf`, undefined, { responseType: 'blob' }),
      filename: `${n.number.replace(/\//g, '_')}.pdf`, title: `${label(n)} ${n.number}`, message: msg, phone: (c.phone || '').replace(/\D/g, '') });
  });
  const email = safe(async () => { const d = await call('post', `/notes/${n.id}/email`); toast.success(`Emailed to ${d.sent_to}`); });
  const cancel = safe(async () => {
    const reason = window.prompt(`Cancel ${n.number}? Its effect on the balance and stock will be reversed. Reason:`);
    if (reason === null) return;
    await call('post', `/notes/${n.id}/cancel`, { reason });
    toast.success(`${n.number} cancelled`);
    onChanged?.();
  });
  return (
    <div className="flex flex-wrap gap-1.5">
      <a href={pdf} target="_blank" rel="noopener noreferrer" className={`${ghost} !py-1 !px-2 text-xs`} data-testid={`note-pdf-${n.id}`}><FileText size={13} /> PDF</a>
      {canEdit && n.party === 'customer' && n.status === 'issued' && <>
        <button type="button" className={`${ghost} !py-1 !px-2 text-xs`} onClick={whatsapp} data-testid={`note-whatsapp-${n.id}`}><Share2 size={13} /> WhatsApp</button>
        <button type="button" className={`${ghost} !py-1 !px-2 text-xs`} onClick={email} data-testid={`note-email-${n.id}`}><Mail size={13} /> Email</button>
      </>}
      {canEdit && n.status === 'issued' && <button type="button" className={`${ghost} !py-1 !px-2 text-xs !text-red-600`} onClick={cancel} data-testid={`note-cancel-${n.id}`}><Ban size={13} /> Cancel</button>}
    </div>
  );
};

export const NoteRow = ({ n, canEdit, onChanged }) => (
  <div className={`rounded-lg border p-3 text-sm ${n.status === 'cancelled' ? 'opacity-60 border-slate-200' : n.kind === 'credit' ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`} data-testid={`note-row-${n.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <b className="font-mono">{n.number}</b>
      <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${n.kind === 'credit' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{label(n)}</span>
      {n.status === 'cancelled' && <span className="text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 bg-slate-200 text-slate-700" data-testid={`note-cancelled-${n.id}`}>Cancelled</span>}
      <span className="text-slate-500">{n.note_date} · {n.reason}{n.reason_text ? ` — ${n.reason_text}` : ''}</span>
      <b className="ml-auto font-mono" data-testid={`note-total-${n.id}`}>{n.kind === 'credit' ? '−' : '+'} {rs(n.totals.grand_total)}</b>
    </div>
    {(n.refund || n.credit_to_customer > 0) && <p className="text-xs text-slate-600 mt-1">{n.refund ? `Refunded by ${n.refund.method.toUpperCase()} on ${n.refund.date}` : `${rs(n.credit_to_customer)} kept as customer credit`}</p>}
    <div className="mt-2"><NoteActions n={n} canEdit={canEdit} onChanged={onChanged} /></div>
  </div>
);
